import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Grid, Dialog, DialogContent, useTheme } from '@mui/material';
import {
  MarketButton,
  MarketHeader,
  MarketLink,
  MarketList,
} from '@market/react';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import { useUserJourneys } from 'components/context/user_journeys';
import PageLoader from 'components/common/page_loader';
import Offer from 'components/common/offer';
import {
  onCheckoutPageView,
  onCheckoutPageClick,
  pushHistoryWithSearchParams,
  CHECKOUT_PAGE_CLICK_EVENTS,
  onCheckoutPageViewV2,
} from 'components/pages/checkout/utilities';
import { useDebouncedCallback } from 'use-debounce';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { COMPONENT_NAMES } from 'constants/tracking';
import {
  FrustrationSignal,
  UserJourneyName,
  UserJourneyVariant,
} from 'constants/user_journeys';

const OfferSelect = () => {
  const theme = useTheme();
  const history = useHistory();
  const { t, i18n } = useTranslation();

  const { trackingData } = useOrderDetails();
  const { checkoutClient, orderRefresh } = useCheckoutFlow();
  const { userJourneyTracker } = useUserJourneys();

  const [offers, setOffers] = useState([]);
  const [selectedOfferToken, setSelectedOfferToken] = useState(null);
  const [debouncedToken, setDebouncedToken] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [seeDetails, setSeeDetails] = useState(false);

  const fetchOffers = () => {
    checkoutClient
      .fetchOffers()
      .then((data) => {
        const selectedOffer = data.offers.find((o) => o.selected);
        if (selectedOffer) {
          setSelectedOfferToken(selectedOffer.token);
        }
        setOffers(data.offers);
      })
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal: FrustrationSignal.OfferSelectStepOffersFetchError,
        });
        pushHistoryWithSearchParams(history, '/payment_option');
      });
  };

  useEffect(() => {
    if (debouncedToken === null) {
      return;
    }
    const abortController = new AbortController();
    checkoutClient
      .selectOffer(debouncedToken, { signal: abortController.signal })
      .then(() => {
        setProcessing(false);
        orderRefresh();
        userJourneyTracker.setVariant({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          variant: UserJourneyVariant.InstallmentOrder,
        });
      })
      .catch(() => {
        // aborted signal throws an exception, so we need to see if we're in this
        // catch block because the fetch actually failed or it just got aborted
        if (!abortController.signal.aborted) {
          userJourneyTracker.addFrustrationSignal({
            userJourneyName: UserJourneyName.ShopPlaceOrder,
            frustrationSignal:
              FrustrationSignal.OfferSelectStepOfferSelectError,
          });
          pushHistoryWithSearchParams(history, '/payment_option');
        }
      });
    // eslint-disable-next-line consistent-return
    return () => {
      abortController.abort();
    };
  }, [debouncedToken]);

  const debounce = useDebouncedCallback((token) => {
    setDebouncedToken(token);
    return token;
  }, 300);

  useEffect(() => {
    fetchOffers();
    onCheckoutPageView('Financing: Offer Select', trackingData);
    onCheckoutPageViewV2({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT.FINANCING_OFFERS,
      additionalParameters: trackingData,
    });
  }, []);

  if (offers.length === 0) {
    return <PageLoader />;
  }

  const offerSelected = offers.some((o) => o.token === selectedOfferToken);

  const optionRows = offers.map((o) => (
    <Offer
      key={o.token}
      o={o}
      disabled={processing && !offerSelected}
      showSubtitle={o.token === selectedOfferToken}
    />
  ));

  const onChange = (newValue) => {
    if (newValue !== null && newValue !== selectedOfferToken) {
      setProcessing(true);
      onCheckoutPageClick({
        eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.offer_selected,
        trackingData,
        additionalDetails: { offer_token: newValue },
        componentName: 'Offer Select',
        actionItem: 'select_offer',
      });
      setSelectedOfferToken(newValue);
      // if these 2 are equal it means the previous call already selected this token, so bail
      // the useEffect on [debouncedToken] will not be triggered because its value will not have changed
      if (newValue === debounce.callback(newValue)) {
        setProcessing(false);
      }
    }
  };

  return (
    <>
      <Box component="h2" sx={{ mb: 2 }}>
        {t('checkout.offerSelect.title')}
      </Box>
      <MarketList
        interactive
        onMarketListSelectionsDidChange={(event) =>
          onChange(event.detail.newSelectionValue)
        }
        value={selectedOfferToken}
      >
        {optionRows}
      </MarketList>
      <Grid container spacing={2} justifyContent="flex-end">
        <Grid item xs={12} md={6}>
          <MarketButton
            {...(processing && { disabled: true })}
            id="change-payment-method-btn"
            onClick={() =>
              pushHistoryWithSearchParams(history, '/payment_option')
            }
            style={{ width: '100%' }}
          >
            {t('checkout.label.changePaymentMethod')}
          </MarketButton>
        </Grid>
        <Grid item xs={12} md={6}>
          <MarketButton
            // Disable the button if an offer has not been selected (happens on first go-around)
            {...((selectedOfferToken === null || processing) && {
              disabled: true,
            })}
            id="continue-to-application-btn"
            rank="primary"
            onClick={() => {
              onCheckoutPageClick({
                eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.offer_select_continue,
                trackingData,
                additionalDetails: {
                  button_text: t('checkout.label.continueToApplication'),
                },
                componentName: 'Offer Select',
                actionItem: 'continue_to_financing',
              });
              pushHistoryWithSearchParams(history, '/finance_app');
            }}
            style={{ width: '100%' }}
          >
            {t('checkout.label.continueToApplication')}
          </MarketButton>
        </Grid>
      </Grid>
      <p className="paragraph-10" style={{ margin: theme.spacing(1, 0) }}>
        {t('checkout.offerSelect.disclaimer')}
        {i18n.exists('checkout.offerSelect.footerLegal') && (
          <MarketLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setSeeDetails(true);
            }}
            className="paragraph-10"
          >
            {` ${t('checkout.label.seeDetails')}`}
          </MarketLink>
        )}
      </p>
      {i18n.exists('checkout.offerSelect.footerLegal') && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={seeDetails}
          onClose={() => setSeeDetails(false)}
        >
          <MarketHeader
            compact
            showNavigation
            onMarketHeaderNavigate={() => setSeeDetails(false)}
            style={{ margin: theme.spacing(4, 4, 0) }}
          >
            <h1>{t('checkout.label.creditSalePlans')}</h1>
          </MarketHeader>
          <DialogContent sx={{ px: 4, pb: 4 }}>
            <p>{t('checkout.offerSelect.footerLegal')}</p>
          </DialogContent>
        </Dialog>
      )}
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default OfferSelect;
